import { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { getSurveyById, getSurveyByInviteToken, sendCompletedSurvey, sendCompletedSurveyByInviteToken } from '../../../API/surveys';
import { ImageTypes } from '../../../enums';
import { AnswerType } from '../survey.types';

export interface OrderedQuestion {
  text: string;
  order: { realOverallIndex: number; orderedIndex: number };
  version: string;
  realCategoryIndex: number;
  category: string;
}

interface Question {
  text: string;
  order: { realOverallIndex: number; orderedIndex: number };
  version: string;
}

interface OrderedResults {
  contact: { answers: number[]; type: string; inverse: number[] };
  achievement: { answers: number[]; type: string; inverse: number[] };
  power: { answers: number[]; type: string; inverse: number[] };
}

export interface CheckBox {
  checked: boolean;
}

type SurveyStep = 'loading' | 'error' | 'welcome' | 'image' | 'text' | 'questionnaire' | 'complete';

const IMAGE_FOLDER_CLASSIC = '/dashboard/images/motive/pseClassic/';
const IMAGE_FOLDER_MODERN = '/dashboard/images/motive/pseModern/';

const IMAGE_SETS: Record<string, string[]> = {
  [ImageTypes.CLASSIC_IMAGE]: [
    `${IMAGE_FOLDER_CLASSIC}laboratory.png`,
    `${IMAGE_FOLDER_CLASSIC}captain.png`,
    `${IMAGE_FOLDER_CLASSIC}bridge.png`,
    `${IMAGE_FOLDER_CLASSIC}circus.png`,
  ],
  [ImageTypes.MODERN_IMAGE]: [
    `${IMAGE_FOLDER_MODERN}laboratory.jpg`,
    `${IMAGE_FOLDER_MODERN}captain.jpg`,
    `${IMAGE_FOLDER_MODERN}bridge.jpg`,
    `${IMAGE_FOLDER_MODERN}circus.jpg`,
  ],
  [ImageTypes.EXTENDED_IMAGE]: [
    `${IMAGE_FOLDER_CLASSIC}laboratory.png`,
    `${IMAGE_FOLDER_CLASSIC}captain.png`,
    `${IMAGE_FOLDER_CLASSIC}bridge.png`,
    `${IMAGE_FOLDER_CLASSIC}circus.png`,
    `${IMAGE_FOLDER_CLASSIC}bar.png`,
    `${IMAGE_FOLDER_CLASSIC}boxer.png`,
  ],
  fallback: [
    `${IMAGE_FOLDER_CLASSIC}laboratory.png`,
    `${IMAGE_FOLDER_CLASSIC}captain.png`,
    `${IMAGE_FOLDER_CLASSIC}office.png`,
    `${IMAGE_FOLDER_CLASSIC}circus.png`,
  ],
};

const TOTAL_QUESTION_COUNT = 30;

function getImageUrls(surveyType: string): string[] {
  return IMAGE_SETS[surveyType] || IMAGE_SETS.fallback;
}

function mixQuestions(questionsToMix: OrderedQuestion[]): OrderedQuestion[] {
  const orderedQuestions: OrderedQuestion[] = [];
  questionsToMix.forEach((question) => {
    orderedQuestions[question.order.orderedIndex] = question;
  });
  return orderedQuestions;
}

function invert(value: number): number {
  return value >= 0 && value <= 5 ? 5 - value : -1;
}

export function useSurveyFlow() {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();

  // Survey state
  const [step, setStep] = useState<SurveyStep>('loading');
  const [alreadyCompleted, setAlreadyCompleted] = useState(false);
  const [surveyType, setSurveyType] = useState(ImageTypes.MODERN_IMAGE);
  const [tenantId, setTenantId] = useState<string>();

  // Welcome
  const [isChecked, setIsChecked] = useState<CheckBox>({ checked: false });

  // Image + Text flow
  const [imageIndex, setImageIndex] = useState(0);
  const [textIndex, setTextIndex] = useState(0);
  const [texts, setTexts] = useState<string[]>([]);

  // Questionnaire
  const [selectedValue, setSelectedValue] = useState<number[]>([]);
  const [results, setResults] = useState<OrderedResults>({
    contact: { answers: [], type: 'contact', inverse: [] },
    achievement: { answers: [], type: 'achievement', inverse: [] },
    power: { answers: [], type: 'power', inverse: [] },
  });
  const [questionsApplyVersion, setQuestionsApplyVersion] = useState<OrderedQuestion[]>([]);
  const [questionsImportanceVersion, setQuestionsImportanceVersion] = useState<OrderedQuestion[]>([]);
  const [allQuestionsAnswered, setAllQuestionsAnswered] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [missingQuestionIndexes, setMissingQuestionIndexes] = useState<number[]>([]);

  // Build questions from translations
  const questionsContact: Question[] = [
    { text: t('surveyQuestionnaireContactOne'), order: { realOverallIndex: 0, orderedIndex: 5 }, version: AnswerType.APPLIES },
    { text: t('surveyQuestionnaireContactTwo'), order: { realOverallIndex: 1, orderedIndex: 12 }, version: AnswerType.APPLIES },
    { text: t('surveyQuestionnaireContactThree'), order: { realOverallIndex: 2, orderedIndex: 10 }, version: AnswerType.APPLIES },
    { text: t('surveyQuestionnaireContactFour'), order: { realOverallIndex: 3, orderedIndex: 4 }, version: AnswerType.APPLIES },
    { text: t('surveyQuestionnaireContactFive'), order: { realOverallIndex: 4, orderedIndex: 1 }, version: AnswerType.APPLIES },
    { text: t('surveyQuestionnaireContactSix'), order: { realOverallIndex: 5, orderedIndex: 9 }, version: AnswerType.APPLIES },
    { text: t('surveyQuestionnaireContactSeven'), order: { realOverallIndex: 6, orderedIndex: 14 }, version: AnswerType.APPLIES },
    { text: t('surveyQuestionnaireContactEight'), order: { realOverallIndex: 7, orderedIndex: 7 }, version: AnswerType.APPLIES },
    { text: t('surveyQuestionnaireContactNine'), order: { realOverallIndex: 8, orderedIndex: 27 }, version: AnswerType.IMPORTANT },
    { text: t('surveyQuestionnaireContactTen'), order: { realOverallIndex: 9, orderedIndex: 21 }, version: AnswerType.IMPORTANT },
  ];
  const questionsAchievement: Question[] = [
    { text: t('surveyQuestionnaireAchievementOne'), order: { realOverallIndex: 10, orderedIndex: 2 }, version: AnswerType.APPLIES },
    { text: t('surveyQuestionnaireAchievementTwo'), order: { realOverallIndex: 11, orderedIndex: 11 }, version: AnswerType.APPLIES },
    { text: t('surveyQuestionnaireAchievementThree'), order: { realOverallIndex: 12, orderedIndex: 28 }, version: AnswerType.IMPORTANT },
    { text: t('surveyQuestionnaireAchievementFour'), order: { realOverallIndex: 13, orderedIndex: 17 }, version: AnswerType.IMPORTANT },
    { text: t('surveyQuestionnaireAchievementFive'), order: { realOverallIndex: 14, orderedIndex: 23 }, version: AnswerType.IMPORTANT },
    { text: t('surveyQuestionnaireAchievementSix'), order: { realOverallIndex: 15, orderedIndex: 15 }, version: AnswerType.IMPORTANT },
    { text: t('surveyQuestionnaireAchievementSeven'), order: { realOverallIndex: 16, orderedIndex: 26 }, version: AnswerType.IMPORTANT },
    { text: t('surveyQuestionnaireAchievementEight'), order: { realOverallIndex: 17, orderedIndex: 19 }, version: AnswerType.IMPORTANT },
    { text: t('surveyQuestionnaireAchievementNine'), order: { realOverallIndex: 18, orderedIndex: 20 }, version: AnswerType.IMPORTANT },
    { text: t('surveyQuestionnaireAchievementTen'), order: { realOverallIndex: 19, orderedIndex: 22 }, version: AnswerType.IMPORTANT },
  ];
  const questionsPower: Question[] = [
    { text: t('surveyQuestionnairePowerOne'), order: { realOverallIndex: 20, orderedIndex: 3 }, version: AnswerType.APPLIES },
    { text: t('surveyQuestionnairePowerTwo'), order: { realOverallIndex: 21, orderedIndex: 0 }, version: AnswerType.APPLIES },
    { text: t('surveyQuestionnairePowerThree'), order: { realOverallIndex: 22, orderedIndex: 8 }, version: AnswerType.APPLIES },
    { text: t('surveyQuestionnairePowerFour'), order: { realOverallIndex: 23, orderedIndex: 13 }, version: AnswerType.APPLIES },
    { text: t('surveyQuestionnairePowerFive'), order: { realOverallIndex: 24, orderedIndex: 6 }, version: AnswerType.APPLIES },
    { text: t('surveyQuestionnairePowerSix'), order: { realOverallIndex: 25, orderedIndex: 16 }, version: AnswerType.IMPORTANT },
    { text: t('surveyQuestionnairePowerSeven'), order: { realOverallIndex: 26, orderedIndex: 18 }, version: AnswerType.IMPORTANT },
    { text: t('surveyQuestionnairePowerEight'), order: { realOverallIndex: 27, orderedIndex: 29 }, version: AnswerType.IMPORTANT },
    { text: t('surveyQuestionnairePowerNine'), order: { realOverallIndex: 28, orderedIndex: 25 }, version: AnswerType.IMPORTANT },
    { text: t('surveyQuestionnairePowerTen'), order: { realOverallIndex: 29, orderedIndex: 24 }, version: AnswerType.IMPORTANT },
  ];

  // --- Initial load ---

  useEffect(() => {
    const checkSurvey = async () => {
      const query = new URLSearchParams(location.search);
      const qTenantId = query.get('tenantId');
      if (qTenantId) {
        setTenantId(qTenantId);
      }
      const surveyRes = qTenantId ? await getSurveyById(id || '', qTenantId) : await getSurveyByInviteToken(id || '');
      if (surveyRes.error) {
        setStep('error');
        return;
      }

      setAlreadyCompleted(surveyRes.data.completed);
      setSurveyType(surveyRes.data.type);

      if (surveyRes.data.completed) {
        setStep('error');
      } else {
        setStep('welcome');
      }

      const oldText = localStorage.getItem(`surveyTexts${id}`);
      if (oldText) {
        setTexts(JSON.parse(oldText));
      }
    };
    checkSurvey();
  }, []);

  // --- Build and shuffle questions (re-run when language changes) ---

  useEffect(() => {
    const allQuestions: OrderedQuestion[] = [];
    questionsContact.forEach((q, i) => allQuestions.push({ ...q, realCategoryIndex: i, category: 'contact' }));
    questionsAchievement.forEach((q, i) => allQuestions.push({ ...q, realCategoryIndex: i, category: 'achievement' }));
    questionsPower.forEach((q, i) => allQuestions.push({ ...q, realCategoryIndex: i, category: 'power' }));

    const ordered = mixQuestions(allQuestions);
    setQuestionsApplyVersion(ordered.filter((q) => q.version === AnswerType.APPLIES));
    setQuestionsImportanceVersion(ordered.filter((q) => q.version === AnswerType.IMPORTANT));
  }, [i18n.language]);

  // --- Track if all questions answered ---

  useEffect(() => {
    if (selectedValue.length === 0 || selectedValue.length < TOTAL_QUESTION_COUNT) {
      setAllQuestionsAnswered(false);
      return;
    }
    const answeredCount = selectedValue.filter((v) => v !== undefined).length;
    setAllQuestionsAnswered(answeredCount === TOTAL_QUESTION_COUNT);
  }, [selectedValue]);

  // --- Derived values ---

  const imageUrls = getImageUrls(surveyType);
  const currentImage = imageUrls[imageIndex];

  // --- Handlers ---

  const handleChangeIsChecked = (name: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsChecked({ ...isChecked, [name]: event.target.checked });
  };

  const handleStartSurvey = () => {
    setStep('image');
  };

  const handleWriteText = () => {
    if (texts[textIndex] === undefined) {
      setTexts((prev) => [...prev, '']);
    }
    setStep('text');
  };

  const handleSaveTextAndNextImage = () => {
    if (imageIndex < imageUrls.length - 1) {
      setTextIndex((prev) => prev + 1);
      setImageIndex((prev) => prev + 1);
      setStep('image');
    } else {
      setStep('questionnaire');
    }
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const updated = [...texts];
    updated[textIndex] = e.target.value;
    setTexts(updated);
  };

  let typingTimer: ReturnType<typeof setTimeout>;

  const handleTextKeyUp = () => {
    clearTimeout(typingTimer);
    typingTimer = setTimeout(() => {
      localStorage.setItem(`surveyTexts${id}`, JSON.stringify(texts));
    }, 300);
  };

  const handleTextKeyDown = () => {
    clearTimeout(typingTimer);
  };

  const selectionChangeHandlerGenerator = (shuffledIdx: number, realIdx: number, realType: 'contact' | 'achievement' | 'power') => {
    return (e: React.ChangeEvent<HTMLInputElement>) => {
      const updateSelection = [...selectedValue];
      updateSelection[shuffledIdx] = parseInt(e.target.value, 10);
      setSelectedValue(updateSelection);
      setMissingQuestionIndexes((prev) => prev.filter((idx) => idx !== shuffledIdx));
      if (submissionError) {
        setSubmissionError(null);
      }

      setResults((prev) => {
        const updated = { ...prev };
        updated[realType] = { ...updated[realType], answers: [...updated[realType].answers] };
        updated[realType].answers[realIdx] = parseInt(e.target.value, 10);
        return updated;
      });
    };
  };

  const handleInvertResults = (result: { answers: number[]; inverse: number[] }) => {
    const newResults = [...result.answers];
    result.inverse.forEach((index) => {
      if (newResults[index] !== undefined) {
        newResults[index] = invert(newResults[index]);
      }
    });
    return newResults;
  };

  const handleSendSurvey = async () => {
    const missingIndexes = Array.from({ length: TOTAL_QUESTION_COUNT }, (_, idx) => idx).filter(
      (idx) => selectedValue[idx] === undefined
    );
    if (missingIndexes.length > 0) {
      setMissingQuestionIndexes(missingIndexes);
      setSubmissionError(t('surveyQuestionnaireMissingAnswersError', { count: missingIndexes.length }));
      return;
    }

    const newContact = handleInvertResults(results.contact);
    const newAchievement = handleInvertResults(results.achievement);
    const newPower = handleInvertResults(results.power);

    const surveyRes = tenantId
      ? await sendCompletedSurvey(id || '', tenantId, texts, newContact, newAchievement, newPower)
      : await sendCompletedSurveyByInviteToken(id || '', texts, newContact, newAchievement, newPower);

    if (!surveyRes.error && surveyRes.data === true) {
      localStorage.removeItem(`surveyTexts${id}`);
      setStep('complete');
      return;
    }

    const backendMessage =
      (surveyRes.error as { message?: string; error?: string } | undefined)?.message ||
      (surveyRes.error as { message?: string; error?: string } | undefined)?.error;
    setSubmissionError(backendMessage || t('surveyQuestionnaireSubmitError'));
  };

  return {
    // State
    step,
    alreadyCompleted,
    isChecked,
    currentImage,
    currentText: texts[textIndex] ?? '',
    allQuestionsAnswered,
    submissionError,
    missingQuestionIndexes,
    questionsApplyVersion,
    questionsImportanceVersion,
    selectedValue,

    // Handlers
    handleChangeIsChecked,
    handleStartSurvey,
    handleWriteText,
    handleSaveTextAndNextImage,
    handleTextChange,
    handleTextKeyUp,
    handleTextKeyDown,
    selectionChangeHandlerGenerator,
    handleSendSurvey,
  };
}
